import { useEffect, useState, useSyncExternalStore } from "react";
import { toast } from "sonner";
import { ApiError, notifyPaymentRequired, PAYMENT_REQUIRED_CODES } from "@/lib/api-client";
import { SyncContext } from "./context";
import { SyncEngine } from "./engine";

type SyncProviderProps = {
	userId: string;
	workspaceId: string;
	/** Rendered until local data is hydrated (instant) or, on first run, bootstrapped. */
	fallback: React.ReactNode;
	children: React.ReactNode;
};

/** Mount with `key={workspaceId}` so switching workspace opens a fresh local database. */
export function SyncProvider({ userId, workspaceId, fallback, children }: SyncProviderProps) {
	const [engine] = useState(
		() =>
			new SyncEngine({
				userId,
				workspaceId,
				onReject: (message, _tx, code) => {
					if (code && PAYMENT_REQUIRED_CODES.has(code))
						return notifyPaymentRequired(new ApiError(402, message, code));
					toast.error("Change not saved", { description: message });
				},
			}),
	);

	useEffect(() => {
		void engine.start();
		return () => engine.stop();
	}, [engine]);

	const { hydrated } = useSyncExternalStore(engine.subscribeStatus, engine.getSnapshot);

	return (
		<SyncContext.Provider value={engine}>{hydrated ? children : fallback}</SyncContext.Provider>
	);
}
